import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, Trash2, Save, X, Edit3, ScrollText, Sparkles, Loader2 } from "lucide-react";

export default function RulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loadingPresets, setLoadingPresets] = useState(false);
  const editorRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/league-rules");
      setRules(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-scroll to editor when it opens
  useEffect(() => {
    if (editing && editorRef.current) {
      setTimeout(() => {
        editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  }, [editing]);

  const startEdit = (rule) => {
    setError("");
    setInfo("");
    setEditing(rule ? { id: rule.id, title: rule.title, description: rule.description } : { title: "", description: "" });
  };

  const onSave = async () => {
    setError("");
    setInfo("");
    if (!editing.title.trim() || !editing.description.trim()) {
      setError("Titolo e descrizione sono obbligatori.");
      return;
    }
    try {
      if (editing.id) {
        await api.put(`/league-rules/${editing.id}`, {
          title: editing.title,
          description: editing.description,
        });
      } else {
        await api.post(`/league-rules`, {
          title: editing.title,
          description: editing.description,
        });
      }
      setEditing(null);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare questa regola?")) return;
    await api.delete(`/league-rules/${id}`);
    load();
  };

  const onLoadPresets = async () => {
    if (!window.confirm(
      "Verranno aggiunte 20 regole standard F1 25 al tuo elenco. Le regole con lo stesso titolo non verranno duplicate. Confermare?"
    )) return;
    setLoadingPresets(true);
    setError("");
    setInfo("");
    try {
      const { data } = await api.post("/league-rules/load-presets");
      setInfo(
        data.inserted > 0
          ? `${data.inserted} regole standard aggiunte${data.skipped ? ` (${data.skipped} già presenti, saltate)` : ""}.`
          : `Tutte le ${data.total_presets} regole standard sono già presenti.`
      );
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingPresets(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-mono-tel text-xs text-yellow-500 uppercase mb-2">[ Regolamento Lega ]</div>
          <h1 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight">
            Regole <span className="text-yellow-500">Personalizzate</span>
          </h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            Definisci sanzioni e principi specifici della tua lega. Verranno applicate{" "}
            <span className="text-yellow-500">automaticamente</span> ad ogni verdetto, in aggiunta al Codice FIA.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onLoadPresets}
            disabled={loadingPresets}
            className="btn-secondary"
            data-testid="load-presets-button"
            title="Aggiungi le 20 regole F1 25 standard come modello"
          >
            {loadingPresets ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Carica 20 regole standard F1 25
          </button>
          <button
            onClick={() => startEdit(null)}
            className="btn-primary"
            data-testid="add-rule-button"
          >
            <Plus className="w-4 h-4" /> Nuova Regola
          </button>
        </div>
      </div>

      {rules.length === 0 && !loading && (
        <div className="card mb-6 border-yellow-500/40 bg-yellow-500/5" data-testid="empty-suggestion">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-yellow-500 flex-none mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-black uppercase text-base text-white mb-1">
                Suggerimento: parti dalle regole standard
              </div>
              <p className="text-zinc-400 text-sm mb-3">
                Carica le <span className="text-yellow-500">20 regole F1 25 preimpostate</span> come punto di partenza:
                Causare collisione, Dive-bomb, Track limits, Moving under braking, Forcing off track, Pit lane, DRS, Safety Car e altre.
                Poi puoi modificarle o eliminarle secondo le esigenze della tua lega.
              </p>
              <button
                onClick={onLoadPresets}
                disabled={loadingPresets}
                className="btn-primary"
                data-testid="empty-load-presets-button"
              >
                {loadingPresets ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Carica le 20 regole standard
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div ref={editorRef} className="card mb-6 border-yellow-500/40" data-testid="rule-editor">
          <div className="section-h">{editing.id ? "Modifica Regola" : "Nuova Regola"}</div>
          <div className="space-y-3">
            <input
              type="text"
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              placeholder="Titolo (es. Patente a Punti)"
              className="input-field"
              data-testid="rule-title-input"
            />
            <textarea
              value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              rows={5}
              placeholder={"Descrizione: condizione che attiva la regola + sanzione.\nEs: 'Cambio di direzione difensiva mentre l'attaccante è in frenata con overlap >50%.\nSanzione: 5 secondi di penalità.'"}
              className="input-field"
              data-testid="rule-description-input"
            />
            <p className="text-xs text-zinc-500 font-mono-tel">
              Tip: scrivi descrizioni con CONDIZIONE + SANZIONE chiare; l'IA matcherà meglio le dinamiche degli incidenti.
            </p>
            {error && (
              <div className="border border-red-500/40 bg-red-500/10 text-red-400 text-sm p-3">{error}</div>
            )}
            <div className="flex items-center gap-2">
              <button onClick={onSave} className="btn-primary" data-testid="rule-save-button">
                <Save className="w-4 h-4" /> Salva
              </button>
              <button onClick={() => setEditing(null)} className="btn-secondary" data-testid="rule-cancel-button">
                <X className="w-4 h-4" /> Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {info && (
        <div className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-sm p-3 mb-4">
          {info}
        </div>
      )}
      {error && !editing && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-400 text-sm p-3 mb-4">{error}</div>
      )}

      {loading ? (
        <div className="font-mono-tel text-zinc-500 text-sm uppercase">Caricamento…</div>
      ) : rules.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-12" data-testid="rules-empty">
          <ScrollText className="w-10 h-10 text-zinc-700 mb-3" />
          <div className="font-display uppercase text-zinc-400 text-xl">Nessuna regola configurata.</div>
          <div className="text-zinc-500 text-sm mt-1">Aggiungi la prima regola della tua lega o carica i preset standard.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="rules-list">
          {rules.map((r) => (
            <div key={r.id} className="card card-hover flex justify-between items-start gap-4">
              <div className="min-w-0">
                <h3 className="font-display font-black uppercase text-xl text-white">{r.title}</h3>
                <p className="text-zinc-400 mt-1 whitespace-pre-wrap text-sm">{r.description}</p>
              </div>
              <div className="flex gap-2 flex-none">
                <button
                  onClick={() => startEdit(r)}
                  className="btn-secondary text-xs"
                  data-testid={`rule-edit-${r.id}`}
                >
                  <Edit3 className="w-3.5 h-3.5" /> Modifica
                </button>
                <button
                  onClick={() => onDelete(r.id)}
                  className="btn-danger text-xs"
                  data-testid={`rule-delete-${r.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Elimina
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
